import { appStateForModalsAtom } from '@/atoms/launchModalsConfigAtom';
import {
  dequeueModalAtom,
  enqueueModalAtom,
  userTriggeredModalsAtom,
} from '@/atoms/modalQueueAtoms';
import ClinicianTypeSelect, {
  OTHER_SPECIALTY_ID,
  OTHER_SPECIALTY_NAME,
} from '@/components/ui/select/clinician-type-select';
import {
  LOCAL_PRIVACY_POLICY,
  LOCAL_TERMS_OF_USE,
  PRIVACY_POLICY,
  TERMS_OF_USE,
} from '@/components/utils/legalDocuments';
import { useQuerySpecialties } from '@/hooks/query/useQuerySpecialties';
import { useUserCountryCode } from '@/hooks/query/useUserCountryCode';
import { useQueuedModal } from '@/hooks/useQueuedModal';
import { ModalPriority } from '@/types/modal-queue.types';
import { isOnPrem } from '@/utils/buildProfile';
import type { HcpVerificationModalPorts } from '@heidi/ads';
import { ModalId } from '@heidi/schema/ml-scribe';
import { useAtomValue, useSetAtom } from 'jotai';
import { selectAtom } from 'jotai/utils';

/**
 * App-side binding of `@heidi/ads`'s HCP-verification modal ports: the queue's
 * subscription hook by reference, its enqueue/dequeue writers as jotai
 * setters, and the two selectAtoms the always-mounted shell reads (selected
 * here so it keeps ignoring unrelated changes). The package's
 * `MODAL_PRIORITY` mirrors the app enum — the co-located suite asserts the
 * values agree.
 */
const isClinicalRecordingActiveAtom = selectAtom(
  appStateForModalsAtom,
  (appState) => appState.isClinicalRecordingActive
);

/** A HIGH entry is the launch-row pin; CRITICAL is the clinician's own open. */
const isPinnedByLaunchRowAtom = selectAtom(userTriggeredModalsAtom, (modals) =>
  modals.some(
    (modal) =>
      modal.id === ModalId.CUSTOM_AUDIENCE_HCP_VERIFICATION_REMINDER_MODAL &&
      modal.priority === ModalPriority.HIGH
  )
);

export const HCP_VERIFICATION_MODAL_PORTS: HcpVerificationModalPorts = {
  useQueuedModal,
  useEnqueueModal: () => useSetAtom(enqueueModalAtom),
  useDequeueModal: () => useSetAtom(dequeueModalAtom),
  useIsClinicalRecordingActive: () => useAtomValue(isClinicalRecordingActiveAtom),
  useIsPinnedByLaunchRow: () => useAtomValue(isPinnedByLaunchRowAtom),
  useCountryCode: useUserCountryCode,
  useSpecialties: (country) =>
    useQuerySpecialties((data) => data.specialties, { country }),
  ClinicianTypeSelect,
  otherSpecialty: { id: OTHER_SPECIALTY_ID, name: OTHER_SPECIALTY_NAME },
  legalLinks: {
    termsHref: isOnPrem ? LOCAL_TERMS_OF_USE : TERMS_OF_USE,
    privacyHref: isOnPrem ? LOCAL_PRIVACY_POLICY : PRIVACY_POLICY,
    openInNewTab: !isOnPrem,
  },
};
